import { hongKongDay } from '../../toll/campaignRules';
import { roundMoney } from '../../toll/money';
import React, { useEffect, useMemo, useState } from "react";
import { Form, Input, InputNumber, Button, DatePicker, message } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { observer } from "mobx-react-lite";
import PageBox from "../../layout/PageBox";
import { MyColor } from "../../config/myStyle";
import { MyInput } from "../../components/MyInput";
import { useLocation, useNavigate } from "react-router-dom";
import { FormRequiredMark } from "../../components/FormRequiredMark";
import { MyUpload } from "../../components/MyUpload";
import moment from "moment";
import api, { PostAssetUploadParameters } from "../../api";
import { campaignStore } from "../../stores";
import { useTranslation } from "react-i18next";

const inputStyle: React.CSSProperties = {
  borderRadius: 8,
  fontSize: 15,
};

// 與後端 CampaignBaseDTO.subTitle 的 600 字元上限保持一致。
const CAMPAIGN_DESCRIPTION_LIMIT = 600;

type UploadFileValue = {
  status: string
  thumbUrl: string
  uid: string
  url: string
  lastModified: number
  lastModifiedDate: string
  name: string
  size: number
  type: string
  webkitRelativePath: string
}

const PostAssetUpload = async (params: PostAssetUploadParameters) => {
  const res = await api.PostAssetUpload(params);
  if (res.kind !== "ok") {
    throw new Error(res.message);
  }
  return res.data;
};

const CampaignCreateForm = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const campaignThemeId = location.state?.campaignThemeId as string | undefined;
  const expectedLaunchDate = Form.useWatch("expectedLaunchDate", form);
  const fundraisingDeadline = Form.useWatch("fundraisingDeadline", form);
  const campaignDescription = Form.useWatch("subTitle", form) ?? "";
  // 直接從表單內容計算餘額，輸入、貼上及刪除都會同步更新。
  const descriptionRemaining = Math.max(0, CAMPAIGN_DESCRIPTION_LIMIT - campaignDescription.length);
  const [loading, setLoading] = useState(false);

  const launchMoment = useMemo(() => {
    if (!expectedLaunchDate) {
      return null;
    }
    return moment(expectedLaunchDate.toISOString());
  }, [expectedLaunchDate]);

  const endMoment = useMemo(() => {
    if (!launchMoment || !fundraisingDeadline) {
      return null;
    }
    return launchMoment.clone().add(Number(fundraisingDeadline), "d");
  }, [fundraisingDeadline, launchMoment]);

  const onFinish = async (values: any) => {
    if (!campaignThemeId) {
      navigate("/campaign/create/select-type");
      return;
    }
    try {
      setLoading(true);
      const coverImage: UploadFileValue | undefined = values.coverImage?.[0];
      const profileImage: UploadFileValue | undefined = values.profileImage?.[0];
      const campaignAssets: { assetId: string; position: "cover" | "profile" }[] = [];

      if (coverImage) {
        const coverImageRes = await PostAssetUpload({ files: coverImage });
        campaignAssets.push({ assetId: coverImageRes.id, position: "cover" as const });
      }

      if (profileImage) {
        const profileImageRes = await PostAssetUpload({ files: profileImage });
        campaignAssets.push({ assetId: profileImageRes.id, position: "profile" as const });
      }

      await campaignStore.createCampaign({
        campaignAssets: campaignAssets.length ? campaignAssets : undefined,
        campaignThemeId,
        // 创建活动接口字段按当前约定映射：筹办者名称=hostName，筹款专页标题=title，活动介绍及支持方法=subTitle。
        expectedEndDate: endMoment?.toISOString(),
        expectedLaunchDate: launchMoment?.toISOString(),
        goalAmount: roundMoney(values.goalAmount),
        hostName: values.hostName,
        subTitle: values.subTitle,
        title: values.title,
      });
    } catch (error: any) {
      message.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!campaignThemeId) {
      navigate("/campaign/create/select-type");
    }
  }, [campaignThemeId, navigate]);

  useEffect(() => {
    if (campaignStore.createCampaigSuccess) {
      navigate("/campaign/create/success", { state: { campaignThemeId } });
      campaignStore.resetCreateCampaign();
    }
  }, [campaignStore.createCampaigSuccess, campaignThemeId, navigate]);

  return (
    <PageBox spinning={loading || campaignStore.createCampaignStatus.loading}>
      <div className="flex flex-col">
        <div
          style={{
            color: MyColor.blue,
            fontSize: 20,
            fontWeight: 600,
            textAlign: "center",
            marginTop: 25,
          }}
        >
          {t("campaign_create_form.personal_info_statement")}
        </div>
        <div className="m-[20px] rounded-[20px] bg-[white] p-[20px]">
          <Form
            scrollToFirstError={true}
            layout="vertical"
            form={form}
            onFinish={onFinish}
            requiredMark={FormRequiredMark}
            initialValues={{
              fundraisingDeadline: 30,
            }}
          >
            <Form.Item
              label={t("campaign_create_form.organizer_name")}
              name="hostName"
              rules={[{ required: true, message: t("campaign_create_form.enter_organizer_name") }]}
            >
              <MyInput
                style={inputStyle}
                maxLength={120}
                placeholder={t("campaign_create_form.organizer_name")}
              />
            </Form.Item>

            <Form.Item
              label={t("campaign_create_form.campaign_title")}
              name="title"
              rules={[{ required: true, message: t("campaign_create_form.enter_campaign_title") }]}
            >
              <MyInput
                style={inputStyle}
                maxLength={120}
                placeholder={t("campaign_create_form.campaign_title")}
              />
            </Form.Item>

            <Form.Item
              name="goalAmount"
              label={t("campaign_create_form.funding_goal")}
              rules={[
                { required: true, message: t("campaign_create_form.enter_funding_goal") },
                {
                  validator: (_, value) => {
                    if (value === undefined || value === null || value === "") {
                      return Promise.resolve();
                    }
                    if (Number(value) < 100) {
                      return Promise.reject(new Error(t("campaign_create_form.goal_must_be_at_least_100")));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
              extra={
                <div className="flex items-center space-x-[2px] text-[rgba(0,0,0,0.8)] text-[11px] font-[450]">
                  <ExclamationCircleOutlined style={{ color: MyColor.red, fontSize: 10 }} />
                  <div>{t("campaign_create_form.funding_goal_hint")}</div>
                </div>
              }
            >
              <InputNumber
                prefix="HK$"
                min={100}
                className="h-[50px] w-full"
              />
            </Form.Item>

            <Form.Item
              name="fundraisingDeadline"
              label={t("campaign_create_form.fundraising_deadline")}
              extra={t("campaign_create_form.fixed_days")}
              rules={[{ required: true, message: t("campaign_create_form.enter_target_end_date") }]}
            >
              <InputNumber min={1} max={60} className="h-[50px] w-full" />
            </Form.Item>

            <Form.Item
              name="expectedLaunchDate"
              label={t("campaign_create_form.campaign_start_date")}
              rules={[{ required: true, message: t("campaign_create_form.select_target_launch_date") }]}
            >
              <DatePicker
                disabledDate={date => !!date && date.format("YYYY-MM-DD") < hongKongDay(new Date())}
                placeholder={t("campaign_create_form.campaign_start_date")}
                className="h-[50px] w-full"
              />
            </Form.Item>

            <Form.Item label={t("campaign_create_form.campaign_end_date")}>
              <div
                className="flex h-[50px] items-center rounded-[8px] border border-[#d9d9d9] px-[12px]"
                style={{ color: "rgba(0,0,0,0.65)" }}
              >
                {endMoment ? endMoment.format("YYYY-MM-DD") : "-"}
              </div>
            </Form.Item>

            <Form.Item
              label={t("campaign_create_form.campaign_subtitle")}
              // 活动介绍及支持方法对应接口的 subTitle。
              name="subTitle"
              extra={<span aria-live="polite">{t("campaign_create_form.campaign_description_hint", { count: descriptionRemaining })}</span>}
              rules={[
                { required: true, message: t("campaign_create_form.enter_campaign_subtitle") },
                { max: CAMPAIGN_DESCRIPTION_LIMIT, message: t("campaign_create_form.campaign_description_too_long", { limit: CAMPAIGN_DESCRIPTION_LIMIT }) },
              ]}
            >
              <Input.TextArea
                maxLength={CAMPAIGN_DESCRIPTION_LIMIT}
                autoSize={{ minRows: 5, maxRows: 8 }}
                placeholder={t("campaign_create_form.campaign_subtitle")}
                style={inputStyle}
              />
            </Form.Item>

            <Form.Item
              name="coverImage"
              label={t("campaign_create_form.campaign_cover_image")}
              extra={t("campaign_create_form.optional_upload_hint")}
            >
              <MyUpload aspect={16 / 9} maxCount={1} />
            </Form.Item>

            <Form.Item
              name="profileImage"
              label={t("campaign_create_form.campaign_profile_image")}
              extra={t("campaign_create_form.optional_upload_hint")}
            >
              <MyUpload aspect={1} maxCount={1} />
            </Form.Item>

            <Form.Item>
              <div className="flex flex-col">
                <Button
                  type="primary"
                  htmlType="submit"
                  style={{
                    width: 125,
                    alignSelf: "center",
                    borderRadius: 22,
                    background: MyColor.sub,
                    height: 44,
                    fontSize: 18,
                    color: "white",
                    fontWeight: 500,
                  }}
                >
                  {t("campaign_create_form.submit")}
                </Button>
              </div>
            </Form.Item>
          </Form>
        </div>
      </div>
    </PageBox>
  );
};

export default observer(CampaignCreateForm);
